// Books setup (the books-setup prototype; missing-screens §3): the four steps a company's books
// need before everything posts on its own (chart of accounts, account roles, bank accounts,
// fiscal year) as a river of stones beside the step at hand, each saying what it unlocks. What
// waits to post for a missing account is said at the top (QA-071), with the invoices it holds.
// The owner and the bookkeeper set it up (accounting.setup); anyone else in Books reads it. An account
// nothing has used can be removed (accounts.remove); one the server refuses because it has history is
// offered "Archive it instead" (accounts.update active false).

import { useEffect, useMemo, useState } from 'react';
import { holdsAny } from '../shared/may.ts';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { MoneySheet } from '../shared/MoneySheet.tsx';
import { archiveOffered, archivedText, removedText, removeOutcome } from './accounts-model.ts';
import { IMPORT_PERMS } from './corrections-model.ts';
import { Imports } from './Imports.tsx';
import type { BooksProps } from './props.ts';
import { errorText, Loading, useAct, useQuery } from './ui.tsx';
import { DocLink } from '../shared/DocLink.tsx';
import {
  fiscalYearText,
  GROUPS,
  MONTHS,
  plural,
  roleKey,
  STEPS,
  SUBTYPE_LABEL,
  type Account,
  type BankAccount,
  type Role,
  type Status,
  type Step,
  stepDone,
} from './setup-model.ts';
import './setup.css';
import { scrollFocus } from '../shared/scrollFocus.ts';

export function Setup({ toast, go, held }: BooksProps) {
  const status = useQuery<Status>('accounts.setupStatus', {});
  const accounts = useQuery<{ accounts: Account[] }>('accounts.list', { includeInactive: false });
  const banks = useQuery<{ bankAccounts: BankAccount[]; etransferBankAccountId: string | null }>(
    'bankAccounts.list',
    {},
  );
  const reload = async () => {
    await Promise.all([status.reload(), accounts.reload(), banks.reload()]);
  };
  const s = status.data;
  const canEdit = s?.canEdit ?? false;
  const missing = useMemo(() => (s ? s.roles.filter((r) => !r.accountId) : []), [s]);
  const done = (st: Exclude<Step, 'ready'>): boolean =>
    !!s && stepDone(st, s, banks.data?.bankAccounts.length ?? 0);
  const next = STEPS.find((x) => !done(x.id))?.id ?? null;
  const [step, setStep] = useState<Step | null>(null);
  // Open on the step that's next (or the summary), once the setup has loaded.
  useEffect(() => {
    if (step === null && s && banks.data) setStep(next ?? 'ready');
  }, [s, banks.data, step, next]);

  if (!s || !banks.data || !accounts.data || !step)
    return (
      <div className="books-page setup">
        <div className="setup-layout">
          {/* L-river: the step names are known without data; whether each is done waits (hidden from
              assistive tech until the real, clickable river replaces it) */}
          <div className="setup-river" aria-hidden="true">
            {STEPS.map((st, i) => (
              <div key={st.id} className="setup-reach">
                <div className="setup-stone">
                  <span className="when">Step {i + 1} of 4</span>
                  <span className="title">{st.name}</span>
                </div>
                <p className="setup-gap">
                  <b>Unlocks</b> {st.unlocks}
                </p>
              </div>
            ))}
          </div>
          <section className="sheet panel setup-sheet" aria-label="Books setup">
            <Loading
              error={status.error ?? banks.error ?? accounts.error}
              what="the books setup"
              retry={() => void reload()}
            />
          </section>
        </div>
      </div>
    );

  const stoneLine = (st: Exclude<Step, 'ready'>) => {
    if (st === 'roles' && s.accounts > 0 && missing.length)
      return (
        <span className="state-line bad">
          {plural(missing.length, 'role needs', 'roles need')} an account
        </span>
      );
    if (st === 'fy' && s.posted)
      return (
        <span className="state-line">
          {MONTHS[s.fiscalYearStartMonth - 1]} · locked after the first posting
        </span>
      );
    if (done(st)) {
      const what =
        st === 'chart'
          ? plural(s.accounts, 'account')
          : st === 'roles'
            ? `${s.roles.length} roles mapped`
            : st === 'bank'
              ? plural(banks.data!.bankAccounts.length, 'bank account')
              : fiscalYearText(s.fiscalYearStartMonth);
      return <span className="state-line ok">Done · {what}</span>;
    }
    if (next === st) return <span className="state-line now">Now: the next step</span>;
    return <span className="state-line">Not yet</span>;
  };

  const props = { s, canEdit, toast, reload, setStep };
  const blocker = missing.find((r) => r.role === 'accounts_receivable') ?? missing[0];
  return (
    <div className="books-page setup">
      {!canEdit && (
        <div className="banner info" role="status">
          <b>The owner or bookkeeper sets this up.</b>
          <span>You can see the setup, not change it.</span>
        </div>
      )}
      {s.accounts > 0 && blocker && (
        <div className="banner bad" role="alert">
          <b>
            {blocker.blocks} until {blocker.label} ({blocker.tech}) has an account.
          </b>
          <span>
            Pick an account in{' '}
            <button className="lnk" type="button" onClick={() => setStep('roles')}>
              Account roles
            </button>{' '}
            and save.
          </span>
        </div>
      )}
      {s.waiting.total > 0 && (
        <div className="banner warn" role="status">
          <b>
            {s.waiting.invoiceCount > 0
              ? `${plural(s.waiting.invoiceCount, 'invoice is', 'invoices are')} waiting to post.`
              : `${plural(s.waiting.total, 'posting is', 'postings are')} waiting to post.`}
          </b>
          <span>They post on their own once the role has an account.</span>
          {s.waiting.invoices.length > 0 && (
            <details className="waiting">
              <summary>See the waiting postings</summary>
              <ul>
                {s.waiting.invoices.map((i) => (
                  <li key={i.id}>
                    Invoice <DocLink doc={{ kind: 'invoice', id: i.id }}>{i.number}</DocLink>
                  </li>
                ))}
                {s.waiting.total > s.waiting.invoices.length && (
                  <li className="muted">
                    and {plural(s.waiting.total - s.waiting.invoices.length, 'other posting')}
                  </li>
                )}
              </ul>
            </details>
          )}
        </div>
      )}
      {/* the steps' sheets carry h3 titles; this keeps the heading order h1 → h2 → h3 */}
      <h2 className="sr-only">The four steps</h2>
      <div className="setup-layout">
        <nav className="setup-river" aria-label="What’s left to do">
          {STEPS.map((st, i) => (
            <div key={st.id} className="setup-reach">
              <button
                type="button"
                className={`setup-stone${done(st.id) ? ' done' : ''}${next === st.id ? ' now' : ''}`}
                aria-current={step === st.id ? 'step' : undefined}
                onClick={() => setStep(st.id)}
              >
                <span className="when">Step {i + 1} of 4</span>
                <span className="title">{st.name}</span>
                {stoneLine(st.id)}
              </button>
              <p className="setup-gap">
                <b>Unlocks</b> {st.unlocks}
              </p>
            </div>
          ))}
          {!next && (
            <button
              type="button"
              className="setup-stone done"
              aria-current={step === 'ready' ? 'step' : undefined}
              onClick={() => setStep('ready')}
            >
              <span className="title">Books ready</span>
              <span className="state-line ok">Everything posts on its own</span>
            </button>
          )}
        </nav>
        <section
          className="sheet panel setup-sheet"
          aria-label={step === 'ready' ? 'Books ready' : STEPS.find((x) => x.id === step)!.name}
        >
          {step === 'chart' && <ChartStep {...props} accounts={accounts.data.accounts} />}
          {step === 'roles' && <RolesStep {...props} accounts={accounts.data.accounts} />}
          {step === 'bank' && (
            <BankStep
              {...props}
              accounts={accounts.data.accounts}
              banks={banks.data.bankAccounts}
              etransferId={banks.data.etransferBankAccountId}
            />
          )}
          {step === 'fy' && <FiscalYearStep {...props} />}
          {step === 'ready' && (
            <div className="ready">
              <h3>The books are ready</h3>
              <p className="muted">
                Invoices, payments and bills now post on their own. Here’s what you set up.
              </p>
              <div className="tiles">
                <div className="tile">
                  <b className="big">{s.accounts}</b>
                  <span className="tile-label">accounts</span>
                </div>
                <div className="tile">
                  <b className="big">{s.roles.length}</b>
                  <span className="tile-label">roles mapped</span>
                </div>
                <div className="tile">
                  <b className="big">{banks.data.bankAccounts.length}</b>
                  <span className="tile-label">
                    {banks.data.bankAccounts.length === 1 ? 'bank account' : 'bank accounts'}
                  </span>
                </div>
                <div className="tile">
                  <b className="big">{MONTHS[s.fiscalYearStartMonth - 1]!.slice(0, 3)}</b>
                  <span className="tile-label">year starts</span>
                </div>
              </div>
              <div className="row-actions">
                <button className="btn primary" type="button" onClick={() => go('banking')}>
                  Go to Banking
                </button>
              </div>
              <p className="muted small">You can come back here any time from Books › Setup.</p>
            </div>
          )}
        </section>
      </div>
      {/* gap #17: the old chart, opening balances and history summaries from the old books (Imports.tsx) */}
      {holdsAny(held, IMPORT_PERMS) && <Imports toast={toast} canChart={canEdit} onChart={reload} />}
    </div>
  );
}

interface StepProps {
  s: Status;
  canEdit: boolean;
  toast: (m: string) => void;
  reload: () => Promise<void>;
  setStep: (s: Step) => void;
}

function ChartStep({ s, canEdit, toast, reload, setStep, accounts }: StepProps & { accounts: Account[] }) {
  const act = useAct(toast, reload);
  const [names, setNames] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState({ code: '', name: '', subtype: 'expense' });
  const [removing, setRemoving] = useState<Account | null>(null);
  if (s.accounts === 0)
    return (
      <div className="offer">
        <h3>Start from the Canada template for home-service trades</h3>
        <p className="muted">
          {s.template.length} accounts a plumbing, heating or electrical company uses, with every posting role
          already pointed at the right one. You can rename and add accounts after.
        </p>
        <div className="acclist">
          {GROUPS.map(([label, subtypes]) => (
            <div key={label} className="accgroup">
              <h4>{label}</h4>
              {s.template
                .filter((a) => subtypes.includes(a.subtype))
                .map((a) => (
                  <div key={a.code} className="acc">
                    <span className="code">{a.code}</span>
                    <span>{a.name}</span>
                  </div>
                ))}
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="row-actions">
            <button
              className="btn primary"
              type="button"
              disabled={act.busy}
              onClick={() =>
                void act
                  .run('accounts.installTemplate', { country: 'CA' }, 'The template is installed.')
                  .then((r) => {
                    if (r) setStep('roles');
                  })
              }
            >
              {act.busy ? 'Installing…' : 'Install the template'}
            </button>
          </div>
        )}
      </div>
    );
  const rename = (a: Account) => {
    const name = (names[a.id] ?? a.name).trim();
    if (!name || name === a.name) return;
    void act.run(
      'accounts.update',
      { id: a.id, name, rowVersion: a.rowVersion },
      `${a.code} is now ${name}.`,
    );
  };
  return (
    <>
      <h3>Chart of accounts</h3>
      <p className="muted">
        {plural(accounts.length, 'account')}. Rename any of them; codes are a convention, not meaning.
        {s.posted
          ? ' After the first posting an account’s type can’t change, but renaming is still fine.'
          : ''}
      </p>
      <div className="table-wrap" ref={scrollFocus}>
        <table className="grid-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Type</th>
              {canEdit && (
                <th>
                  <span className="sr-only">Remove</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id}>
                <td className="code">{a.code}</td>
                <td>
                  {canEdit ? (
                    <input
                      className="field"
                      aria-label={`Name of account ${a.code}`}
                      value={names[a.id] ?? a.name}
                      onChange={(e) => setNames({ ...names, [a.id]: e.target.value })}
                      onBlur={() => rename(a)}
                      onKeyDown={(e) => {
                        // Enter saves by leaving the field: one save, never two with the same version.
                        if (e.key === 'Enter') e.currentTarget.blur();
                      }}
                    />
                  ) : (
                    a.name
                  )}
                </td>
                <td>{SUBTYPE_LABEL[a.subtype] ?? a.subtype}</td>
                {canEdit && (
                  <td>
                    <button
                      type="button"
                      className="btn quiet sm"
                      aria-label={`Remove account ${a.code} ${a.name}`}
                      onClick={() => setRemoving(a)}
                    >
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <form
          className="fgrid"
          aria-label="Add an account"
          onSubmit={(e) => {
            e.preventDefault();
            void act
              .run(
                'accounts.create',
                { code: draft.code.trim(), name: draft.name.trim(), subtype: draft.subtype },
                `${draft.code.trim()} ${draft.name.trim()} is added.`,
              )
              .then((r) => {
                if (r) setDraft({ code: '', name: '', subtype: draft.subtype });
              });
          }}
        >
          <label>
            Code
            <input
              className="field"
              inputMode="numeric"
              placeholder="1020"
              value={draft.code}
              onChange={(e) => setDraft({ ...draft, code: e.target.value })}
            />
          </label>
          <label>
            Name
            <input
              className="field"
              placeholder="Bank: savings"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </label>
          <label>
            Type
            <select
              className="field"
              value={draft.subtype}
              onChange={(e) => setDraft({ ...draft, subtype: e.target.value })}
            >
              {Object.entries(SUBTYPE_LABEL).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <div className="row-actions">
            <button
              className="btn"
              type="submit"
              disabled={act.busy || !draft.code.trim() || !draft.name.trim()}
            >
              Add account
            </button>
          </div>
        </form>
      )}
      <div className="row-actions">
        <button className="btn primary" type="button" onClick={() => setStep('roles')}>
          Continue to account roles
        </button>
      </div>
      {removing && (
        <RemoveAccount
          account={removing}
          onClose={() => setRemoving(null)}
          onDone={async (said) => {
            setRemoving(null);
            toast(said);
            await reload();
          }}
        />
      )}
    </>
  );
}

/** Remove an account nothing has used; the server says when one has history, and then it can be archived. */
function RemoveAccount({
  account: a,
  onClose,
  onDone,
}: {
  account: Account;
  onClose: () => void;
  onDone: (said: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offerArchive, setOfferArchive] = useState(false);
  const run = async (archive: boolean) => {
    setBusy(true);
    setError(null);
    try {
      if (archive) {
        await client.call('accounts.update', { id: a.id, active: false, rowVersion: a.rowVersion });
        await onDone(archivedText(a));
      } else {
        await client.call('accounts.remove', { id: a.id });
        await onDone(removedText(a));
      }
    } catch (e) {
      setError(errorText(e));
      if (!archive) setOfferArchive(e instanceof RpcError && archiveOffered(e));
      else setOfferArchive(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title={`Remove ${a.code} ${a.name}?`}
      outcome={removeOutcome(a)}
      confirm="Remove it"
      ready={!offerArchive}
      busy={busy}
      error={offerArchive ? null : error}
      note={
        offerArchive && (
          <div className="row end archive-instead">
            <p className="error" role="alert">
              {error}
            </p>
            <button type="button" className="btn primary" disabled={busy} onClick={() => void run(true)}>
              Archive it instead
            </button>
          </div>
        )
      }
      onConfirm={() => void run(false)}
      onClose={onClose}
    />
  );
}

function RolesStep({ s, canEdit, toast, reload, accounts }: StepProps & { accounts: Account[] }) {
  const act = useAct(toast, reload);
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [errs, setErrs] = useState<Record<string, string>>({});
  if (s.accounts === 0)
    return (
      <>
        <h3>Account roles</h3>
        <p className="muted">Install or import a chart first; the roles point at its accounts.</p>
      </>
    );
  const value = (r: Role) => picks[roleKey(r)] ?? r.accountId ?? '';
  const row = (r: Role) => {
    const v = value(r);
    const options = accounts.filter((a) => !r.types.length || r.types.includes(a.type));
    return (
      <div key={roleKey(r)} className={`rolerow${v ? '' : ' missing'}`}>
        <div className="rl">
          <b>{r.label}</b>
          <span>{r.tech}</span>
          {!v && <span className="needs">Needs an account</span>}
        </div>
        <select
          className="field"
          aria-label={`Account for ${r.label}`}
          disabled={!canEdit}
          value={v}
          onChange={(e) => setPicks({ ...picks, [roleKey(r)]: e.target.value })}
        >
          <option value="">Choose an account…</option>
          {options.map((a) => (
            <option key={a.id} value={a.id}>
              {a.code} {a.name}
            </option>
          ))}
        </select>
        {errs[roleKey(r)] && (
          <span className="err why" role="alert">
            {errs[roleKey(r)]}
          </span>
        )}
      </div>
    );
  };
  const changed = s.roles.filter((r) => picks[roleKey(r)] && picks[roleKey(r)] !== r.accountId);
  const save = async () => {
    const failed: Record<string, string> = {};
    for (const r of changed) {
      const ok = await act.run('accounts.mapRole', {
        role: r.role,
        key: r.key,
        accountId: picks[roleKey(r)],
      });
      if (!ok) failed[roleKey(r)] = `${r.label} can’t use that account.`;
    }
    setErrs(failed);
    if (!Object.keys(failed).length) {
      setPicks({});
      toast(changed.length ? 'Roles saved.' : 'Nothing to save.');
    }
  };
  const main = s.roles.filter((r) => r.main);
  const rest = s.roles.filter((r) => !r.main);
  const restMissing = rest.filter((r) => !value(r)).length;
  return (
    <>
      <h3>Account roles</h3>
      <p className="muted">
        The accounts the posting rules use. The template pointed each one at the usual account; check the ones
        you care about.
      </p>
      <div>{main.map(row)}</div>
      <details className="more" open={restMissing > 0}>
        <summary>
          {rest.length} more roles{restMissing ? `, ${restMissing} need an account` : ', all set'}
        </summary>
        {rest.map(row)}
      </details>
      <p className="muted small">
        Sales for a single department use <b>Sales</b> unless you give one its own income account later.
      </p>
      {canEdit && (
        <div className="row-actions">
          <button className="btn primary" type="button" disabled={act.busy} onClick={() => void save()}>
            {act.busy ? 'Saving…' : 'Save roles'}
          </button>
        </div>
      )}
    </>
  );
}

function BankStep({
  s,
  canEdit,
  toast,
  reload,
  accounts,
  banks,
  etransferId,
}: StepProps & { accounts: Account[]; banks: BankAccount[]; etransferId: string | null }) {
  const act = useAct(toast, reload);
  const ledger = accounts.filter((a) => a.subtype === 'bank' || a.subtype === 'credit_card');
  const [d, setD] = useState({ name: '', institution: '', last4: '', accountId: '' });
  const [err, setErr] = useState<string | null>(null);
  if (s.accounts === 0)
    return (
      <>
        <h3>Bank accounts</h3>
        <p className="muted">
          Install or import a chart first; a bank account points at a bank-type account in it, a company card
          at a credit card account.
        </p>
      </>
    );
  const accountId = d.accountId || ledger[0]?.id || '';
  const code = (id: string) => accounts.find((a) => a.id === id)?.code ?? '';
  const add = () => {
    if (!d.name.trim()) return setErr('Give the account a name.');
    if (d.last4 && !/^\d{4}$/.test(d.last4)) return setErr('The last 4 digits are four digits, like 4821.');
    if (!accountId) return setErr('Add a bank or credit card account to the chart first.');
    setErr(null);
    void act
      .run(
        'bankAccounts.create',
        {
          name: d.name.trim(),
          accountId,
          ...(d.institution.trim() ? { institution: d.institution.trim() } : {}),
          ...(d.last4 ? { last4: d.last4 } : {}),
        },
        `${d.name.trim()} is added.`,
      )
      .then((r) => {
        if (r) setD({ name: '', institution: '', last4: '', accountId: '' });
      });
  };
  return (
    <>
      <h3>Bank accounts</h3>
      <p className="muted">
        The accounts your money moves through. Statements are imported against each one and reconciled to it.
      </p>
      {banks.length ? (
        <div className="banks">
          {banks.map((b) => (
            <div key={b.id} className="bankrow">
              <div>
                <b>{b.name}</b>
                <span>
                  {' '}
                  · {b.institution ?? 'No bank named'}
                  {b.last4 ? ` · ending ${b.last4}` : ''}
                </span>
              </div>
              <span>Ledger {code(b.accountId)} · statements by CSV or OFX</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="muted">No bank accounts yet.</p>
      )}
      {banks.length > 0 && (
        // where customer e-transfers post (payments.etransferBankAccountId): the first added until one is chosen
        <label className="wide etransfer-in">
          E-transfers arrive in
          {canEdit ? (
            <select
              className="field"
              value={etransferId ?? banks[0]!.id}
              disabled={act.busy}
              onChange={(e) =>
                void act.run(
                  'bankAccounts.setEtransferAccount',
                  { bankAccountId: e.target.value },
                  `E-transfers now arrive in ${banks.find((b) => b.id === e.target.value)?.name ?? 'that account'}.`,
                )
              }
            >
              {banks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {b.last4 ? ` · ending ${b.last4}` : ''}
                </option>
              ))}
            </select>
          ) : (
            <b> {banks.find((b) => b.id === etransferId)?.name ?? banks[0]!.name}</b>
          )}
        </label>
      )}
      {canEdit && (
        <form
          className="fgrid"
          aria-label={banks.length ? 'Add another bank account' : 'Add a bank account'}
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <label>
            Name
            <input
              className="field"
              placeholder="RBC operating"
              value={d.name}
              onChange={(e) => setD({ ...d, name: e.target.value })}
            />
          </label>
          <label>
            Bank
            <input
              className="field"
              placeholder="RBC Royal Bank"
              value={d.institution}
              onChange={(e) => setD({ ...d, institution: e.target.value })}
            />
          </label>
          <label>
            Last 4 digits
            <input
              className="field"
              inputMode="numeric"
              maxLength={4}
              placeholder="4821"
              value={d.last4}
              onChange={(e) => setD({ ...d, last4: e.target.value })}
            />
          </label>
          <label className="wide">
            Ledger account
            <select
              className="field"
              value={accountId}
              onChange={(e) => setD({ ...d, accountId: e.target.value })}
            >
              {ledger.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} {a.name}
                </option>
              ))}
            </select>
          </label>
          <p className="muted small help">
            Only bank and credit card accounts. Need another? Add one in the chart.
          </p>
          {err && (
            <p className="err" role="alert">
              {err}
            </p>
          )}
          <div className="row-actions">
            <button className="btn primary" type="submit" disabled={act.busy}>
              {act.busy ? 'Adding…' : 'Add bank account'}
            </button>
          </div>
        </form>
      )}
    </>
  );
}

function FiscalYearStep({ s, canEdit, toast, reload }: StepProps) {
  const act = useAct(toast, reload);
  const [month, setMonth] = useState(s.fiscalYearStartMonth);
  const locked = s.posted;
  return (
    <>
      <h3>Fiscal year</h3>
      <p className="muted">
        The month your year starts. Earnings before it move to retained earnings on the balance sheet.
      </p>
      <label className="fy">
        The year starts in
        <select
          className="field"
          value={month}
          disabled={locked || !canEdit}
          onChange={(e) => setMonth(Number(e.target.value))}
        >
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </select>
      </label>
      <p className="fysentence">
        Your year runs <b>{fiscalYearText(month)}</b>
      </p>
      {locked ? (
        <div className="banner info">
          <b>Can’t change after the first posting.</b>
          <span>Every report and the year-end close depend on it. Account names can still change.</span>
        </div>
      ) : (
        canEdit && (
          <div className="row-actions">
            <button
              className="btn primary"
              type="button"
              disabled={act.busy}
              onClick={() =>
                void act.run(
                  'accounts.setFiscalYear',
                  { startMonth: month },
                  `The year starts in ${MONTHS[month - 1]}.`,
                )
              }
            >
              {act.busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        )
      )}
    </>
  );
}
